import React, { useState } from "react";
import { toast } from "sonner";
import { api, isSessionExpired } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";

/**
 * Retry-confirmation dialog. Displays how many rows are failed and surfaces
 * the server's `exhausted_row_ids` count post-retry — those rows have hit the
 * shared `IMPORT_MAX_ROW_ATTEMPTS` limit and won't be re-queued.
 */
export function RetryConfirmDialog({
  job,
  open,
  onClose,
  onRetried,
}: {
  job: { id: string; counts?: Record<string, number> };
  open: boolean;
  onClose: () => void;
  onRetried: () => void;
}) {
  const [inFlight, setInFlight] = useState(false);
  const [exhausted, setExhausted] = useState<number | null>(null);
  const failed = Number((job.counts ?? {}).failed ?? 0);

  const doRetry = async () => {
    if (inFlight) return;
    setInFlight(true);
    try {
      const { data } = await api.post<{ exhausted_row_ids?: string[] }>(
        `/import/jobs/${job.id}/retry`,
      );
      const ex = Array.isArray(data.exhausted_row_ids) ? data.exhausted_row_ids.length : 0;
      if (ex > 0) setExhausted(ex);
      toast.success("Retry queued");
      onRetried();
      if (ex === 0) onClose();
    } catch (e: unknown) {
      if (isSessionExpired(e)) return;
      toast.error("Could not retry.");
    } finally {
      setInFlight(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="retry-confirm-dialog" aria-describedby="retry-desc">
        <DialogHeader>
          <DialogTitle>Retry failed rows?</DialogTitle>
          <DialogDescription id="retry-desc">
            Rows within the per-row attempt limit will be re-queued for the worker. Rows
            already at the maximum attempt count are exhausted and stay failed.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm" data-testid="retry-counts">
          <div>
            Failed rows currently: <strong>{failed}</strong>
          </div>
          {exhausted !== null && (
            <div className="mt-1 text-amber-800" data-testid="retry-exhausted-line">
              {exhausted} row{exhausted === 1 ? "" : "s"} exhausted their retry budget.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            className="rounded-full"
            onClick={onClose}
            disabled={inFlight}
            data-testid="retry-confirm-cancel-btn"
          >
            Close
          </Button>
          <Button
            className="rounded-full"
            onClick={doRetry}
            disabled={inFlight}
            data-testid="retry-confirm-go-btn"
          >
            {inFlight ? "Queuing…" : "Yes, retry failed rows"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
